import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useTranslation } from "../../../hooks/useTranslation";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AddResponse } from "./AddRestrictionDialog";

type AddResultDialogProps = {
  addResponse: AddResponse | null;
  open: boolean;
  onClose: () => void;
};

export const AddResultDialog = (props: AddResultDialogProps) => {
  const { open, onClose, addResponse } = props;
  const countries = useMarketplaceStore((state) => state.countries);
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.storeSecurity.restrictions.restrictionResult",
  );

  const dataWithCountry = addResponse?.restrictionsSavingReports?.map(
    (report) => ({
      ...report,
      country: countries.find((country) => country.id === report.marketplaceId),
    }),
  );

  if (!countries || !addResponse) return null;

  const groupedCountryErrors =
    addResponse.restrictionsSavingReports?.reduce(
      (acc, report) => acc + report.countOfExistingRestrictions,
      0,
    ) ?? 0;

  const totalErrors =
    addResponse.countOfDuplicateKeywords +
    (addResponse.incorrectKeywords?.length ?? 0) +
    groupedCountryErrors;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{translate("title")}</DialogTitle>
      <DialogContent>
        <Alert sx={{ marginBottom: "8px" }} severity="info">
          <Typography variant="body2">
            {translate("description", {
              error: totalErrors,
            })}
          </Typography>
        </Alert>
        {addResponse.countOfDuplicateKeywords > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  {translate("duplicateTitle", {
                    count: addResponse.countOfDuplicateKeywords,
                  })}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {addResponse.duplicateKeywords.map((keyword) => (
                    <ListItem key={keyword}>
                      <ListItemText
                        primary={keyword}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </div>
        )}
        {addResponse.incorrectKeywords &&
          addResponse.incorrectKeywords.length > 0 && (
            <div style={{ marginBottom: "8px" }}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    {translate("incorrectTitle", {
                      count: addResponse.incorrectKeywords.length,
                    })}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {addResponse.incorrectKeywords.map((keyword) => (
                      <ListItem key={keyword}>
                        <ListItemText
                          primary={keyword}
                          primaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </div>
          )}
        {dataWithCountry && dataWithCountry.length > 0 && (
          <div>
            {dataWithCountry.map((report) =>
              report.countOfExistingRestrictions > 0 ? (
                <Accordion key={`${report.marketplaceId}`}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex">
                      <img
                        src={report.country?.flagUrl + "?width=48"}
                        alt={report.country?.countryName}
                        width="24px"
                        style={{ marginRight: 8, objectFit: "contain" }}
                      />
                      <Typography>
                        {translate("alreadyExistsTitle", {
                          country: report.country?.countryName,
                          count: report.countOfExistingRestrictions,
                        })}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{ maxHeight: "400px", overflowY: "scroll" }}
                  >
                    <List>
                      {report.existingRestrictions.map((keyword) => (
                        <ListItem key={keyword}>
                          <ListItemText
                            primary={keyword}
                            primaryTypographyProps={{ variant: "body2" }}
                            secondary={translate("alreadyExists")}
                            secondaryTypographyProps={{ variant: "caption" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ) : null,
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {translateWithoutPrefix("buttons.ok")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
